// @ts-nocheck
import React from 'react';
import { Alert, Tag, List, Button } from 'antd';
import { 
  WarningOutlined, 
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  BugOutlined
} from '@ant-design/icons';
import { colors, spacing } from '../design-system/tokens';
import { Typography } from '../design-system/typography';
import { ConflictInfo } from './SmartSnippetBuilder';

interface ConflictWarningProps {
  conflicts: ConflictInfo[];
  onResolve?: (conflict: ConflictInfo, resolution: string) => void;
}

export const ConflictWarning: React.FC<ConflictWarningProps> = ({
  conflicts,
  onResolve
}) => {
  if (conflicts.length === 0) {
    return null;
  }

  // Group conflicts by type
  const incompatibleConflicts = conflicts.filter(c => c.type === 'incompatible');
  const duplicateConflicts = conflicts.filter(c => c.type === 'duplicate');  
  const missingRequiredConflicts = conflicts.filter(c => c.type === 'missing_required');

  // Determine overall severity
  const severity = incompatibleConflicts.length > 0 ? 'error' : 
                   missingRequiredConflicts.length > 0 ? 'warning' : 'info';

  const getConflictIcon = (type: string) => {
    switch (type) {
      case 'incompatible':
        return <ExclamationCircleOutlined style={{ color: colors.status.negative }} />;
      case 'missing_required':
        return <WarningOutlined style={{ color: colors.status.warning }} />;
      case 'duplicate':
        return <InfoCircleOutlined style={{ color: colors.primary[60] }} />;
      default:
        return <BugOutlined />;
    }
  };

  const getConflictTypeLabel = (type: string) => {
    switch (type) {
      case 'incompatible':
        return 'Incompatible Snippets';
      case 'missing_required':
        return 'Missing Required';
      case 'duplicate':
        return 'Duplicate Content';
      default:
        return 'Unknown Conflict';
    }
  };

  const getConflictTypeColor = (type: string) => {
    switch (type) {
      case 'incompatible':
        return 'red';
      case 'missing_required':
        return 'orange';
      case 'duplicate':
        return 'blue';
      default:
        return 'default';
    }
  };

  return (
    <Alert
      type={severity}
      showIcon
      icon={getConflictIcon(conflicts[0].type)}
      message={
        <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
          <Typography variant="label" style={{ color: colors.text.primary, margin: 0 }}>
            {conflicts.length === 1 ? '1 Conflict Detected' : `${conflicts.length} Conflicts Detected`}
          </Typography>
          <div style={{ display: 'flex', gap: spacing.xs }}>
            {incompatibleConflicts.length > 0 && (
              <Tag color="red" style={{ fontSize: '10px', margin: 0 }}>
                {incompatibleConflicts.length} Incompatible
              </Tag>
            )}
            {missingRequiredConflicts.length > 0 && (
              <Tag color="orange" style={{ fontSize: '10px', margin: 0 }}>
                {missingRequiredConflicts.length} Missing Required
              </Tag>
            )}
            {duplicateConflicts.length > 0 && (
              <Tag color="blue" style={{ fontSize: '10px', margin: 0 }}>
                {duplicateConflicts.length} Duplicates
              </Tag>
            )}
          </div>
        </div>
      }
      description={
        <div style={{ marginTop: spacing.sm }}>
          <List
            size="small"
            dataSource={conflicts}
            renderItem={(conflict, index) => (
              <List.Item
                style={{
                  padding: spacing.sm,
                  backgroundColor: index % 2 === 0 ? 'transparent' : `${colors.background.tertiary}50`,
                  borderRadius: '4px',
                  border: 'none'
                }}
                actions={[
                  conflict.suggestion && onResolve && (
                    <Button
                      type="link"
                      size="small"
                      onClick={() => onResolve(conflict, 'auto')}
                      style={{ fontSize: '11px', padding: 0 }}
                    >
                      Auto-resolve
                    </Button>
                  )
                ].filter(Boolean)}
              >
                <List.Item.Meta
                  avatar={getConflictIcon(conflict.type)}
                  title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
                      <Tag 
                        color={getConflictTypeColor(conflict.type)}
                        style={{ fontSize: '10px', margin: 0 }}
                      >
                        {getConflictTypeLabel(conflict.type)}
                      </Tag>
                      <Typography 
                        variant="label" 
                        style={{ 
                          fontSize: '12px', 
                          color: colors.text.primary,
                          margin: 0
                        }}
                      >
                        Affected Snippets: {conflict.snippetIds.join(', ')}
                      </Typography>
                    </div>
                  }
                  description={
                    <div style={{ marginTop: spacing.xs }}>
                      <Typography 
                        variant="value" 
                        style={{ 
                          fontSize: '11px',
                          color: colors.text.secondary,
                          lineHeight: '1.4',
                          marginBottom: conflict.suggestion ? spacing.xs : 0
                        }}
                      >
                        {conflict.description}
                      </Typography>
                      
                      {conflict.suggestion && (
                        <div style={{
                          padding: spacing.xs,
                          backgroundColor: `${colors.primary[40]}15`,
                          borderRadius: '4px',
                          borderLeft: `3px solid ${colors.primary[40]}`
                        }}>
                          <Typography 
                            variant="value" 
                            style={{ 
                              fontSize: '11px',
                              color: colors.primary[60],
                              fontStyle: 'italic',
                              margin: 0
                            }}
                          >
                            💡 Suggestion: {conflict.suggestion}
                          </Typography>
                        </div>
                      )}
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        </div>
      }
      style={{
        border: `1px solid ${getAlertBorderColor(severity)}`,
        borderRadius: '8px'
      }}
    />
  );
};

/**
 * Get alert border color based on severity
 */
function getAlertBorderColor(severity: string): string {
  switch (severity) {
    case 'error':
      return colors.status.negative;
    case 'warning':
      return colors.status.warning;
    case 'info':
      return colors.primary[60];
    default:
      return colors.border.default;
  }
}